import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFetchDetail, usePutDetail } from "@hooks/useFetchDetail";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { openToast } from "@utils/toastStore";
import StaticGroupWizard from "../GroupWizard/StaticGroupWizard";
import {
  buildInitialEnrollConfig,
  StaticGroupWizardData,
} from "../GroupWizard/types";
import { enrollGroupToLearningObjects } from "../enrollGroupToLearningObjects";
import { updateUserStaticGroups } from "../updateUserStaticGroups";

const MEMBERS_PAGE_SIZE = 1000;

const EditStaticGroup: React.FC = () => {
  const { id: corporateId = "", groupId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("group-wizard-v2");
  const backTo = `/v2/corporate/${corporateId}/detail/users-groups?tab=static-groups`;
  const [submitting, setSubmitting] = useState(false);

  const { dataDetail, isLoading } = useFetchDetail(
    `${ServicesURL.staticGroups}/static`,
    groupId,
    PagesID["corporate.groups"],
  );

  const { trigger, setId } = usePutDetail(ServicesURL.staticGroups);
  useEffect(() => {
    if (groupId) setId(groupId);
  }, [groupId, setId]);

  const membersUrl =
    corporateId && groupId
      ? `${ServicesURL.staticGroups}/v3/${corporateId}/static/${groupId}/users`
      : null;
  const { items: members, isLoading: loadingMembers } = usePaginatedList(
    membersUrl,
    { page: 1, pageSize: MEMBERS_PAGE_SIZE },
    { responseKey: "response" },
  );

  const initialData = useMemo<StaticGroupWizardData | null>(
    () =>
      !isLoading && !loadingMembers && dataDetail && !Array.isArray(dataDetail)
        ? {
            name: dataDetail.name || "",
            description: dataDetail.description || "",
            userIds: (members || [])
              .map((m: any) => Number(m.user_id ?? m.id_user ?? m.id))
              .filter((idUser: number) => !Number.isNaN(idUser)),
            loSelections: [],
            enrollConfig: buildInitialEnrollConfig(),
          }
        : null,
    [dataDetail, isLoading, members, loadingMembers],
  );

  const onComplete = async (data: StaticGroupWizardData) => {
    setSubmitting(true);
    try {
      await (trigger as any)({
        name: data.name || "",
        description: data.description || "",
      });
      // TODO (BE Mancante): sostituire il ciclo di PUT per utente con
      // l'endpoint bulk (userIds sull'update del gruppo) quando disponibile.
      const initialIds = new Set(initialData?.userIds ?? []);
      const currentIds = new Set(data.userIds);
      const toAdd = data.userIds.filter((idUser) => !initialIds.has(idUser));
      const toRemove = (initialData?.userIds ?? []).filter(
        (idUser) => !currentIds.has(idUser),
      );
      const ops = [
        ...toAdd.map((idUser) =>
          updateUserStaticGroups(idUser, {
            addGroupIds: [Number(groupId)],
            removeGroupIds: [],
          }),
        ),
        ...toRemove.map((idUser) =>
          updateUserStaticGroups(idUser, {
            addGroupIds: [],
            removeGroupIds: [Number(groupId)],
          }),
        ),
      ];
      if (ops.length > 0) {
        const results = await Promise.allSettled(ops);
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          openToast({
            severity: "error",
            message: String(
              t("usersUpdatePartial", {
                done: ops.length - failed,
                total: ops.length,
              }),
            ),
          });
        }
      }
      await enrollGroupToLearningObjects({
        corporateId,
        groupId,
        groupType: "STATIC",
        loSelections: data.loSelections,
        enrollType: data.enrollConfig.enrollType || "TOP",
        config: data.enrollConfig,
      });
      navigate(backTo);
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  if (!initialData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StaticGroupWizard
      initialData={initialData}
      backTo={backTo}
      onComplete={onComplete}
      isCompleting={submitting}
      title={t("titleEdit")}
      abandonMode="edit"
      groupId={groupId}
    />
  );
};

export default EditStaticGroup;
